import getBatch, { type BatchResponse } from './getBatch';
import getMotionsByGroup, { type MotionRecord } from './getMotionsByGroup';

export type PerformanceCaptureRig = 'axel' | 'mhr';

export const CAPTURE_SOURCE_WAIT_TIMEOUT_MS = 60_000;

type CaptureStatus = {
  batch: BatchResponse;
  motions: MotionRecord[];
  state: 'pending' | 'ready' | 'failed';
  terminal: boolean;
  message?: string;
};

const FAILED_STATUSES = new Set(['FAILED', 'REJECTED', 'CANCELLED', 'FLAGGED']);
const TERMINAL_STATUSES = new Set(['COMPLETED', ...FAILED_STATUSES]);

function hasRequiredSources(motion: MotionRecord, rig: PerformanceCaptureRig) {
  const axel =
    [
      motion.bvhURLs,
      motion.bvh_urls,
      motion.outputs?.map((output) => output.bvhURL || output.bvh || ''),
      motion.bvhURL ? [motion.bvhURL] : undefined,
    ].find((urls) => urls?.some((url) => url.trim())) ?? [];
  const mhr =
    motion.mhrBvhURLs ??
    motion.outputs?.map((output) => output.mhrBvhURL || '') ??
    [];
  const identities =
    motion.mhrIdentityJsonURLs ??
    motion.outputs?.map((output) => output.mhrIdentityJsonURL || '') ??
    [];
  const actorCount = Math.max(
    1,
    motion.outputs?.length ?? 0,
    motion.actorOutputs?.length ?? 0,
    axel.length,
    mhr.length,
  );
  const bvhs = rig === 'axel' ? axel : mhr;
  return (
    bvhs.length === actorCount &&
    bvhs.every((url) => !!url.trim()) &&
    (rig === 'axel' ||
      (identities.length === actorCount &&
        identities.every((url) => !!url.trim())))
  );
}

/** Check the motion sources needed by the editor, independently of ZIP/MP4 exports. */
export default async function getPerformanceCaptureStatus(
  batchID: string,
  apiKey: string,
  signal: AbortSignal,
  rig: PerformanceCaptureRig = 'mhr',
): Promise<CaptureStatus> {
  const { data: batch, error } = await getBatch(batchID, apiKey, signal);
  signal.throwIfAborted();
  if (error || !batch)
    throw error ?? new Error('Unable to check capture status.');
  if (FAILED_STATUSES.has(batch.status))
    return {
      batch,
      motions: [],
      state: 'failed',
      terminal: true,
      message:
        batch.rejectedReason ||
        `Performance capture ${batch.status.toLowerCase()}.`,
    };

  const motions: MotionRecord[] = [];
  let nextToken: string | undefined;
  do {
    const { data, error } = await getMotionsByGroup(
      batchID,
      apiKey,
      nextToken,
      signal,
    );
    signal.throwIfAborted();
    if (error || !data)
      throw error ?? new Error('Unable to load captured motion.');
    motions.push(...data.items);
    nextToken = data.nextToken;
  } while (nextToken);

  const completed = motions.filter((motion) => motion.status === 'COMPLETED');
  const allFinished =
    motions.length > 0 &&
    motions.length >= batch.numTotalJobs &&
    motions.every((motion) => TERMINAL_STATUSES.has(motion.status));
  const terminal = allFinished || batch.status === 'COMPLETED';
  if (
    terminal &&
    motions.length >= batch.numTotalJobs &&
    completed.length &&
    completed.every((motion) => hasRequiredSources(motion, rig))
  )
    return { batch, motions: completed, state: 'ready', terminal };
  if (allFinished && !completed.length)
    return {
      batch,
      motions,
      state: 'failed',
      terminal,
      message:
        motions.find((motion) => motion.errorMessage)?.errorMessage ||
        'No motion could be captured from this video.',
    };
  return { batch, motions, state: 'pending', terminal };
}
